import { useState } from 'react'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, Table, TableHead, TableRow, TableCell, TableBody,
  TextField, Alert, CircularProgress, Typography,
} from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getLotHistory, blockLot, type Lot } from '../../api/stocuri'
import { errorMessage } from '../../api/client'

export default function LotHistoryDialog({ lot, onClose }: { lot: Lot | null; onClose: () => void }) {
  const open = lot !== null
  const qc = useQueryClient()
  const [reason, setReason] = useState('')
  const { data: history } = useQuery({
    queryKey: ['stocuri', 'loturi', lot?.articleId ?? null, lot?.lotNumber ?? null, 'istoric'],
    queryFn: () => getLotHistory(lot!.articleId, lot!.lotNumber), enabled: open,
  })

  const warehouseIds = [...new Set((history ?? []).map((m) => m.warehouseId))]
  const singleWarehouseId = warehouseIds.length === 1 ? warehouseIds[0] : null
  const warehouseName = history?.find((m) => m.warehouseId === singleWarehouseId)?.warehouseName

  const mutation = useMutation({
    mutationFn: () => blockLot({ articleId: lot!.articleId, warehouseId: singleWarehouseId!, lotNumber: lot!.lotNumber, reason }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['stocuri'] })
      setReason('')
      onClose()
    },
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Istoric lot {lot?.lotNumber} — {lot?.articleCode}</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Data</TableCell><TableCell>Depozit</TableCell><TableCell>Zonă/Locație</TableCell>
                <TableCell>Tip</TableCell><TableCell>Status</TableCell><TableCell align="right">Cantitate</TableCell><TableCell>Referință</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {history?.map((m) => (
                <TableRow key={m.id}>
                  <TableCell>{new Date(m.createdAt).toLocaleString('ro-RO')}</TableCell>
                  <TableCell>{m.warehouseName}</TableCell>
                  <TableCell>{m.zoneName ?? '—'} {m.locationCode ? `/ ${m.locationCode}` : ''}</TableCell>
                  <TableCell>{m.type}</TableCell>
                  <TableCell>{m.status}</TableCell>
                  <TableCell align="right">{m.quantity}</TableCell>
                  <TableCell>{m.reference ?? '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {lot && !lot.isExpired && lot.liberQuantity <= 0 ? null : (
            <Stack spacing={1}>
              <Typography variant="subtitle2">Blochează lotul (mută soldul liber în status Blocat)</Typography>
              {!singleWarehouseId && <Alert severity="warning">Lotul apare în mai multe depozite — blocarea manuală nu e suportată din acest ecran.</Alert>}
              {singleWarehouseId && (
                <Stack direction="row" spacing={1.5}>
                  <TextField size="small" label={`Motiv (depozit: ${warehouseName})`} value={reason} onChange={(e) => setReason(e.target.value)} sx={{ flexGrow: 1 }} />
                  <Button variant="outlined" color="warning" disabled={!reason || mutation.isPending} onClick={() => mutation.mutate()}
                    startIcon={mutation.isPending ? <CircularProgress size={14} /> : undefined}>
                    Blochează
                  </Button>
                </Stack>
              )}
              {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose}>Închide</Button>
      </DialogActions>
    </Dialog>
  )
}
